/**
 * Cartoon art palette — one ink, two line weights, light from the upper left.
 * Shared by every procedurally drawn picture (creatures, props, icons, sky, FX).
 */

/** The one outline colour: a deep blue-teal that reads on day sky and night sea. */
export const INK = "#172a3a";
/** Softer ink for inner details (folds, freckles, crack lines). */
export const INK_SOFT = "#2c4458";

/** Outline weights as a share of the subject's radius. */
export const LINE_BODY = 0.065;
export const LINE_DETAIL = 0.035;

/** Where the shade crescent sits: the lit body is shifted up-left by this share of radius. */
export const SHADE_SHIFT = [-0.1, -0.13];
/** Where the glint sits on a round body (share of radius, from the centre). */
export const GLINT_AT = [-0.38, -0.42];

export const WHITE = "#ffffff";
export const CREAM = "#fff8e8";
export const BLUSH = "#ff8fa3";
export const EYE_WHITE = "#ffffff";

/**
 * Base / light / shade triples for flat cel shading. `light` paints the lit upper-left,
 * `shade` the crescent on the lower right.
 */
export const TONES = {
  sky: { base: "#5cc6f2", light: "#a9e6ff", shade: "#3a9fd4" },
  sea: { base: "#2f9fd6", light: "#6fd0f2", shade: "#1f78b0" },
  glass: { base: "#cdefff", light: "#ffffff", shade: "#8fcfe8" },
  stone: { base: "#9a8f8a", light: "#c9c0b8", shade: "#6c625e" },
  gold: { base: "#ffc84a", light: "#fff3b8", shade: "#e08a1c" },
  gem: { base: "#e24bd8", light: "#ff9cf5", shade: "#a42aa0" },
  red: { base: "#ff5a5f", light: "#ff9b9b", shade: "#d2363f" },
  green: { base: "#5fd068", light: "#b2f2a8", shade: "#36a046" },
  mint: { base: "#5fe0a0", light: "#d4ffe6", shade: "#2a9c6a" },
  cream: { base: "#fff3d6", light: "#ffffff", shade: "#f0d8a8" },
  wood: { base: "#d28a46", light: "#f0b878", shade: "#9c5a26" },
  navy: { base: "#2c4d7a", light: "#4f78ad", shade: "#1c3558" },
  violet: { base: "#b49cff", light: "#efe8ff", shade: "#7a5ee0" },
};

/** Puff / effect palettes (base, light, shade, ink) — after Ghost Circuit's TOON set. */
export const PUFF_TONES = {
  hot: { base: "#ffd45c", light: "#fff6c4", shade: "#ff9a2e", ink: INK },
  fire: { base: "#ff9a2e", light: "#ffd45c", shade: "#e0521f", ink: INK },
  ember: { base: "#e0521f", light: "#ff8a3a", shade: "#a8321a", ink: INK },
  smoke: { base: "#d6dbe6", light: "#f6f8fc", shade: "#a9b2c4", ink: "#6b7690" },
  dust: { base: "#c8b28c", light: "#e9dcc0", shade: "#9d8662", ink: "#5e4c34" },
  spirit: { base: "#6fc3ff", light: "#d2f0ff", shade: "#3a86d8", ink: INK },
  foam: { base: "#e8f8ff", light: "#ffffff", shade: "#a8dcf2", ink: "#3a7aa0" },
  ecto: { base: "#5fe0a0", light: "#d4ffe6", shade: "#2a9c6a", ink: INK },
  gold: { base: "#ffc84a", light: "#fff3b8", shade: "#e08a1c", ink: INK },
  pink: { base: "#ff8fd0", light: "#ffd8f0", shade: "#d855a8", ink: INK },
  violet: { base: "#b49cff", light: "#efe8ff", shade: "#7a5ee0", ink: INK },
  cloud: { base: "#ffffff", light: "#ffffff", shade: "#d4e6f6", ink: "#5f86a8" },
  nightCloud: { base: "#5a6f9c", light: "#7f93bf", shade: "#3e5080", ink: "#1c2a4a" },
};

/** Mix two #rrggbb colours (t = 0 → a, 1 → b). */
export function mixHex(a, b, t) {
  var pa = parseHex(a), pb = parseHex(b);
  var k = Math.max(0, Math.min(1, t));
  return toHex(
    pa[0] + (pb[0] - pa[0]) * k,
    pa[1] + (pb[1] - pa[1]) * k,
    pa[2] + (pb[2] - pa[2]) * k
  );
}

/** #rrggbb → [r, g, b] (0–255). */
export function parseHex(hex) {
  var s = String(hex || "#000000").replace("#", "");
  if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
  var n = parseInt(s.slice(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function toHex(r, g, b) {
  var c = function (v) {
    var x = Math.max(0, Math.min(255, Math.round(v))).toString(16);
    return x.length < 2 ? "0" + x : x;
  };
  return "#" + c(r) + c(g) + c(b);
}

/** #rrggbb → 0xrrggbb for Pixi tints. */
export function hexToNumber(hex) {
  var p = parseHex(hex);
  return (p[0] << 16) | (p[1] << 8) | p[2];
}
